import { useState, useEffect, useRef, useCallback, type KeyboardEvent } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Send as SendIcon,
  Stop as StopIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useRoentgenStore } from './roentgenStore';
import { useRoentgenChat } from './useRoentgenChat';
import { useRoentgenContext } from './useRoentgenContext';
import { ChatMessageItem } from './ChatMessageItem';
import { SessionList } from './SessionList';
import { api } from '../../api/rpc';
import { useUIStore, type SortItem } from '../../stores/uiStore';
import { getDefaultViewer } from '../viewer/viewerRegistry';
import type { Study, WhereBean } from '../../api/types';
import type { AssistantAction } from './types';

const HEALTH_TIMEOUT_MS = 2_000;
const INITIAL_HEALTH_RETRY_MS = 10_000;
const MAX_HEALTH_RETRY_MS = 60_000;

const BACKEND_SORT_TO_GRID_FIELD: Record<string, string> = {
  acc: 'acc',
  bod: 'bod',
  comment: 'comment',
  date: 'date',
  descr: 'descr',
  dicomCount: 'dicomCount',
  dob: 'dob',
  id: 'id',
  mod: 'mod',
  name: 'name',
  phy: 'physician',
  radiologist: 'radiologist',
  reason: 'reason',
  sex: 'sex',
  sitename: 'siteName',
  siteName: 'siteName',
  status: 'status',
};

function buildWhereClausesFromRoentgenArgs(args: Record<string, unknown>): WhereBean[] {
  const where: WhereBean[] = [];

  const pushStringEquals = (attr: string, value: unknown) => {
    if (typeof value === 'string' && value.trim()) {
      where.push({ attr, op: 'EQUALS', sval: [value.trim().toUpperCase()] });
    }
  };

  pushStringEquals('mod', args.modality);
  pushStringEquals('status', args.status);
  pushStringEquals('bod', args.bodyPart);
  pushStringEquals('siteId', args.siteId);

  if (typeof args.description === 'string' && args.description.trim()) {
    where.push({ attr: 'descr', op: 'LIKE', sval: [args.description.trim().toUpperCase()] });
  }
  if (
    typeof args.assignedTo === 'string' &&
    args.assignedTo.trim() &&
    !['me', 'self', 'current'].includes(args.assignedTo.trim().toLowerCase())
  ) {
    where.push({ attr: 'radiologist', op: 'EQUALS', sval: [args.assignedTo.trim()] });
  }
  if (typeof args.urgent === 'boolean') {
    where.push({ attr: 'urgentActionNeeded', op: 'EQUALS', sval: [String(args.urgent)] });
  }
  if (typeof args.onHold === 'boolean') {
    where.push({ attr: 'wasHeld', op: 'EQUALS', sval: [String(args.onHold)] });
  }

  return where;
}

function buildSortModelFromRoentgenArgs(args: Record<string, unknown>): SortItem[] {
  if (typeof args.sort !== 'string' || !args.sort.trim()) {
    return [];
  }

  const [rawField, rawDirection = 'asc'] = args.sort.split(':');
  const field = BACKEND_SORT_TO_GRID_FIELD[rawField.trim()] ?? rawField.trim();
  const direction = rawDirection.trim().toLowerCase() === 'desc' ? 'desc' : 'asc';

  if (!field) {
    return [];
  }

  return [{ field, sort: direction }];
}

function buildResultLimitFromRoentgenArgs(args: Record<string, unknown>): number | null {
  if (typeof args.limit !== 'number' || !Number.isFinite(args.limit)) {
    return null;
  }

  return Math.max(1, Math.floor(args.limit));
}

export const RoentgenPanel = () => {
  const close = useRoentgenStore((s) => s.close);
  const activeSessionId = useRoentgenStore((s) => s.activeSessionId);
  const allowUiActions = useRoentgenStore((s) => s.allowUiActions);
  const setAllowUiActions = useRoentgenStore((s) => s.setAllowUiActions);
  const setActiveTab = useUIStore((s) => s.setActiveTab);
  const clearFilters = useUIStore((s) => s.clearFilters);
  const setAdvancedFilters = useUIStore((s) => s.setAdvancedFilters);
  const setQuickSearch = useUIStore((s) => s.setQuickSearch);
  const setSortModel = useUIStore((s) => s.setSortModel);
  const setResultLimit = useUIStore((s) => s.setResultLimit);
  const setSelectedStudyUids = useUIStore((s) => s.setSelectedStudyUids);
  const setSelectedStudies = useUIStore((s) => s.setSelectedStudies);
  const openDynamicTab = useUIStore((s) => s.openDynamicTab);
  const {
    sessions,
    isLoadingSessions,
    pendingActions,
    messages,
    isStreaming,
    closingSessionId,
    error,
    switchSession,
    clearPendingActions,
    sendMessage,
    confirmAction,
    rejectAction,
    cancelStream,
    clearMessages,
    createSession,
    closeSession,
    loadHistory,
  } = useRoentgenChat();
  const context = useRoentgenContext();

  const [input, setInput] = useState('');
  const [healthy, setHealthy] = useState<boolean | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const healthRetryMsRef = useRef(INITIAL_HEALTH_RETRY_MS);
  const shouldReloadHistoryRef = useRef(false);
  const historyLoadedForSessionRef = useRef<string | null>(null);

  // Health check on mount with exponential backoff while unavailable.
  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleRetry = () => {
      if (cancelled) return;
      const nextDelay = healthRetryMsRef.current;
      timeoutId = setTimeout(() => {
        void runHealthCheck();
      }, nextDelay);
      healthRetryMsRef.current = Math.min(nextDelay * 2, MAX_HEALTH_RETRY_MS);
    };

    const runHealthCheck = async () => {
      const controller = new AbortController();
      const abortTimeoutId = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

      try {
        const response = await fetch('/roentgen/health', { signal: controller.signal });
        if (cancelled) return;

        if (response.ok) {
          setHealthy(true);
          healthRetryMsRef.current = INITIAL_HEALTH_RETRY_MS;
        } else {
          shouldReloadHistoryRef.current = true;
          setHealthy(false);
          scheduleRetry();
        }
      } catch {
        if (cancelled) return;
        shouldReloadHistoryRef.current = true;
        setHealthy(false);
        scheduleRetry();
      } finally {
        clearTimeout(abortTimeoutId);
      }
    };

    void runHealthCheck();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Reload active session history once the sidecar is reachable again.
  useEffect(() => {
    if (healthy !== true || !activeSessionId) {
      return;
    }
    if (
      historyLoadedForSessionRef.current === activeSessionId &&
      !shouldReloadHistoryRef.current
    ) {
      return;
    }

    historyLoadedForSessionRef.current = activeSessionId;
    shouldReloadHistoryRef.current = false;
    void loadHistory(activeSessionId);
  }, [activeSessionId, healthy, loadHistory]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getStudyFromAction = useCallback(async (payload: Record<string, unknown>): Promise<Study | null> => {
    const study = payload.study;
    if (study && typeof study === 'object') {
      const candidate = study as Partial<Study>;
      if (typeof candidate.objKey === 'string' && typeof candidate.uid === 'string') {
        return candidate as Study;
      }
    }

    const objKey = typeof payload.objKey === 'string' ? payload.objKey : null;
    if (!objKey) {
      return null;
    }

    try {
      return await api.getStudy(objKey);
    } catch {
      return null;
    }
  }, []);

  const applyFilterAction = useCallback((action: AssistantAction) => {
    const payload = action.payload as {
      args?: Record<string, unknown>;
      clearExisting?: boolean;
    };
    const where = buildWhereClausesFromRoentgenArgs(payload.args ?? {});
    const sortModel = buildSortModelFromRoentgenArgs(payload.args ?? {});
    const resultLimit = buildResultLimitFromRoentgenArgs(payload.args ?? {});

    setActiveTab('studies');
    if (payload.clearExisting !== false) {
      clearFilters();
      setQuickSearch('', 'smart');
      setResultLimit(null);
    }

    setAdvancedFilters(where);
    setSortModel(sortModel);
    setResultLimit(resultLimit);
  }, [clearFilters, setActiveTab, setAdvancedFilters, setQuickSearch, setResultLimit, setSortModel]);

  const selectStudyAction = useCallback((study: Study) => {
    setActiveTab('studies');
    setSelectedStudyUids([study.uid]);
    setSelectedStudies([study]);
  }, [setActiveTab, setSelectedStudies, setSelectedStudyUids]);

  const openViewerAction = useCallback((study: Study, viewerId?: string) => {
    const resolvedViewerId = viewerId ?? getDefaultViewer().id;
    selectStudyAction(study);
    openDynamicTab({
      id: `viewer-${resolvedViewerId}-${study.uid}`,
      label: `${study.name || 'Study'} - ${study.acc || study.uid}`.slice(0, 40),
      type: 'viewer',
      study,
      viewerId: resolvedViewerId,
      closeable: true,
    });
  }, [openDynamicTab, selectStudyAction]);

  const handleAction = useCallback(async (action: AssistantAction) => {
    if (!allowUiActions) {
      return;
    }

    if (action.type === 'apply_filter') {
      applyFilterAction(action);
      return;
    }

    const study = await getStudyFromAction(action.payload);
    if (!study) {
      return;
    }

    if (action.type === 'select_study' || action.type === 'navigate_study') {
      selectStudyAction(study);
      return;
    }

    if (action.type === 'open_viewer') {
      const viewerId =
        typeof action.payload.viewerId === 'string' ? action.payload.viewerId : undefined;
      openViewerAction(study, viewerId);
    }
  }, [allowUiActions, applyFilterAction, getStudyFromAction, openViewerAction, selectStudyAction]);

  useEffect(() => {
    if (pendingActions.length === 0 || !allowUiActions) {
      return;
    }

    pendingActions
      .filter((action) => action.type === 'apply_filter')
      .forEach(applyFilterAction);
    clearPendingActions();
  }, [allowUiActions, applyFilterAction, clearPendingActions, pendingActions]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    const sent = await sendMessage(trimmed, context);
    if (sent) {
      setInput('');
    }
  }, [input, isStreaming, sendMessage, context]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleNewChat = useCallback(async () => {
    clearMessages();
    await createSession();
  }, [clearMessages, createSession]);

  const handleSessionChange = useCallback(async (sessionId: string) => {
    await switchSession(sessionId);
  }, [switchSession]);

  const handleCloseSession = useCallback(async (sessionId: string) => {
    try {
      await closeSession(sessionId);
    } catch {
      // The hook publishes the error banner.
    }
  }, [closeSession]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Roentgen
          </Typography>
          {healthy === false && (
            <Chip label="Offline" size="small" color="error" variant="outlined" />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SessionList
            sessions={sessions}
            activeSessionId={activeSessionId}
            isLoading={healthy === false || isLoadingSessions}
            isStreaming={isStreaming}
            closingSessionId={closingSessionId}
            onChange={(sessionId) => {
              void handleSessionChange(sessionId);
            }}
            onCreate={() => {
              void handleNewChat();
            }}
            onClose={(sessionId) => {
              void handleCloseSession(sessionId);
            }}
          />
          <Tooltip title="Close">
            <IconButton size="small" onClick={close}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
        }}
      >
        {messages.length === 0 && !isStreaming && (
          <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            <Typography variant="body2">
              Ask Roentgen about studies, queues, or system status.
            </Typography>
          </Box>
        )}

        {healthy === false && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Roentgen is temporarily unavailable. The panel will retry automatically.
          </Alert>
        )}

        {messages.map((msg) => (
          <ChatMessageItem
            key={msg.id}
            message={msg}
            onAction={handleAction}
            onConfirm={confirmAction}
            onReject={rejectAction}
            actionsEnabled={allowUiActions}
          />
        ))}

        {isStreaming && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, ml: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Thinking...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Error banner */}
      {error && (
        <Alert
          severity={error.fatal ? 'error' : 'warning'}
          sx={{ mx: 1, mb: 0.5, flexShrink: 0 }}
        >
          {error.message}
        </Alert>
      )}

      {/* Input area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.5,
          px: 1,
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <TextField
          multiline
          maxRows={4}
          size="small"
          fullWidth
          placeholder="Ask about studies, queues, or system status..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={healthy === false}
          inputProps={{ maxLength: 4000 }}
          sx={{ '& .MuiInputBase-root': { fontSize: 13 } }}
        />
        {isStreaming ? (
          <Tooltip title="Stop">
            <IconButton size="small" onClick={cancelStream} color="error">
              <StopIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Send (Enter)">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  void handleSend();
                }}
                disabled={!input.trim() || healthy === false}
                color="primary"
              >
                <SendIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ px: 1, pb: 1, flexShrink: 0 }}>
        <FormControlLabel
          control={(
            <Switch
              size="small"
              checked={allowUiActions}
              onChange={(event) => setAllowUiActions(event.target.checked)}
            />
          )}
          label={<Typography variant="caption">Allow UI actions</Typography>}
          sx={{ ml: 0, mb: 0.25 }}
        />
        <Typography variant="caption" color="text.secondary">
          Select a study and say "the selected study" instead of typing patient names.
        </Typography>
      </Box>
    </Box>
  );
};


